import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Column, usePagination, useSortBy, useTable } from 'react-table';
import { Link } from "react-router-dom";
import { getDefaultPageSize } from "../../../helpers/utils";
import ReactTable from "../../../component/ReactTable";
import { DATA_LOADING } from "../../../component/Constants";

const INTEGRATION_SECTIONS = [
    {
        title: 'Alert notification',
        collapseId: 'collapseAlertNotifications',
        integrations: ['Email', 'PagerDuty', 'ServiceNow', 'Slack', 'Webhook']
    },
    {
        title: 'APM integration',
        collapseId: 'collapseApmIntegrations',
        integrations: ['IBM Instana', 'New Relic']
    },
    {
        title: 'AI integration',
        collapseId: 'collapseAiIntegrations',
        integrations: ['AI Co-pilot']
    }
];

function IntegrationSection(props: { title: string, collapseId: string, loading: number, data: any[], isAdmin: boolean }) {
    const data = props.data;

    const columns: Column[] = useMemo(() => [
        {
            Header: 'Name',
            accessor: 'name',
            className: 'fw-bold integrations-col-name',
            defaultCanSort: true,
        },
        {
            Header: 'Description',
            accessor: 'desc',
            className: 'integrations-col-description',
            defaultCanSort: true,
        },
        {
            Header: 'Defined',
            accessor: 'isSet',
            className: 'integrations-col-defined',
            defaultCanSort: true,
            Cell: ({ cell }) => (
                <React.Fragment>
                    <div className='form-check form-switch'>
                        <input checked={cell.row.values.isSet}
                               type="checkbox"
                               className="form-check-input"
                               disabled={true}
                               data-lpignore={true}
                        />
                    </div>
                </React.Fragment>
            )
        },
        {
            Header: 'Enabled',
            accessor: 'enabled',
            className: 'integrations-col-enabled',
            defaultCanSort: true,
            Cell: ({ cell }) => (
                <React.Fragment>
                    <div className='form-check form-switch'>
                        <input checked={cell.row.values.enabled === 'true'}
                               type="checkbox"
                               className="form-check-input"
                               disabled={true}
                               data-lpignore={true}
                        />
                    </div>
                </React.Fragment>
            )
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions integrations-col-actions',
            disableSortBy: true,
            headerClassName: 'actions integrations-col-actions',
            Cell: ({ cell }) => (
                props.isAdmin ? (
                    <Link to={(cell.row.original as any).url} role="button"
                          className="btn btn-sm btn-primary">
                        <i className="fal fa-edit fa-fw"/><span>Edit</span>
                    </Link>
                ) : null
            )
        }
    ], [props.isAdmin]);

    const tableInstance = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['alertActionId', 'htmlIconCode'],
                pageIndex: 0,
                pageSize: getDefaultPageSize()
            }
        },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    return (
        <div className="card integrations-section">
            <div className="card-header">
                <i className="fal fa-object-ungroup fa-fw" aria-hidden="true"/>
                {props.title}
                <span className="badge bg-info" data-tooltip-id="global-tooltip" data-tooltip-content={`Total ${props.title} count`}>{data.length}</span>
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target={`#${props.collapseId}`} aria-expanded="false" aria-controls={props.collapseId}/>
            </div>
            <div id={props.collapseId} className="card-body collapse show">
                <React.Fragment>
                    <ReactTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={data.length}
                        error={''}
                        loading={props.loading === DATA_LOADING}
                        hidePagination={true}
                    />
                </React.Fragment>
            </div>
        </div>
    )
}

function List(props: { loading: number, refresh: Function, integrations: any, isAdmin: boolean }) {
    const groupedIntegrations = useMemo(() => INTEGRATION_SECTIONS.map((section) => ({
        ...section,
        data: section.integrations
            .map((name) => props.integrations.find((integration: any) => integration.name === name))
            .filter(Boolean)
    })), [props.integrations]);

    return (
        <div className="col h-100">
            {groupedIntegrations.map((section) => (
                <IntegrationSection
                    key={section.title}
                    title={section.title}
                    collapseId={section.collapseId}
                    data={section.data}
                    loading={props.loading}
                    isAdmin={props.isAdmin}
                />
            ))}
        </div>
    )
}

List.propTypes = {
    refresh: PropTypes.func.isRequired,
};

export default List;
